<template>
<div>
  <div>
    <div class="h-panel">
      <b-button
        v-if="mayManage"
        :to="{name: 'derby_add'}"
        variant="primary">
        Schedule a derby
      </b-button>

      <b-button
        :disabled="isDeleteDerbiesButtonDisabled"
        :variant="deleteDerbiesButtonVariant"
        @click="deleteDerbiesWithConfirmation"
      >
        Delete
      </b-button>
    </div>

    <h2>Скачки</h2>

    <table class="mb-2">
      <tr>
        <th class="pr-1">
          <label for="window-past-duration-selector" class="mb-0">Past</label>
        </th>
        <td class="pr-4">
          <b-form-select
            id="window-past-duration-selector"
            v-model="windowPastDuration"
            :options="windowDurationOptions"
          />
        </td>
        <th class="pr-1">
          <label for="window-future-duration-selector" class="mb-0">Future</label>
        </th>
        <td>
          <b-form-select
            id="window-future-duration-selector"
            v-model="windowFutureDuration"
            :options="windowDurationOptions"
          />
        </td>
      </tr>
    </table>

    <p v-if="message !== null">
      {{message}}
    </p>

    <table v-if="hasDerbies" class="table table-bordered">
      <thead class="thead-dark">
        <tr>
          <th>
            <input type="checkbox" v-model="allSelected" class="mr-2">
            Id
          </th>
          <th>Audience</th>
          <th>Status</th>
          <th>Begin<br><small>UTC+3</small></th>
          <th>Join available until<br><small>UTC+3</small></th>
          <th>End<br><small>UTC+3</small></th>
          <th>Min player level</th>
          <th>Min client version</th>
          <th>AB test proto id</th>
          <th>AB test group</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="container in derbyContainers"
          :key="container.derby.id"
          :class="getDerbyClass(container.derby.status)"
        >
          <td class="text-nowrap">
            <input type="checkbox" v-model="container.selected" class="mr-2">
            <derby-link :derbyId="container.derby.id" label="" />
          </td>
          <td>{{ container.derby.audience | getDerbyAudienceNameVerbose }}</td>
          <td :class="statusClass(container.derby.status)">{{ container.derby.status | statusStr }}</td>
          <td>{{ container.derby.start_at | getMomentMinStr }}</td>
          <td>{{ container.derby.finish_at - container.derby.prohibition_interval | getMomentMinStr }}</td>
          <td>{{ container.derby.finish_at | getMomentMinStr }}</td>
          <td>{{ container.derby.min_player_level }}</td>
          <td>{{ container.derby.min_client_version | convertVersionCodeToStr }}</td>
          <td>{{ container.derby.ab_test_id | getHumanProtoAlias}}</td>
          <td>{{ container.derby.ab_test_group }}</td>
          <td class="action-box">
            <b-button size="sm" variant="danger"
              v-if="mayDelete(container.derby)"
              @click="deleteDerbyWithConfirmation(container.derby)">
              Delete
            </b-button>
            <b-button size="sm" variant="warning"
              v-if="mayFinish(container.derby)"
              @click="finishDerbyWithConfirmation(container.derby)">
              Finish
            </b-button>
          </td>
        </tr>
      </tbody>
    </table>

    <loading-indicator v-if="isLoading"/>
  </div>
</div>
</template>

<script>
import auth from '../../auth.js';
import { HTTP } from '../../http-common.js';
import notifier from '../../notifier.js';

import { TIME_UNIT_MONTH          } from '../../model/event_time_filter.js';
import { getWindowDurationOptions } from '../../model/event_time_filter.js';
import { getBegin                 } from '../../model/event_time_filter.js';
import { getEnd                   } from '../../model/event_time_filter.js';

/**
 * @typedef { import('../../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../../service/confirm.js';

import { convertMomentToStamp        } from '../../util.js';
import { isRoleAdmin                 } from '../../util.js';

import { Derby                       } from '../../model/race.js';
import { getDerbyAudienceNameVerbose } from '../../model/race.js';
import { isDerbyDeletable            } from '../../model/race.js';

import LoadingIndicator from '../LoadingIndicator.vue';
import DerbyLink from './DerbyLink.vue';

const LOADING_STATUS__INIT    = 1;
const LOADING_STATUS__LOADING = 2;
const LOADING_STATUS__NORMAL  = 3;
const LOADING_STATUS__ERROR   = 4;

export default {
  name: 'DerbyList',

  components: {
    LoadingIndicator,
    DerbyLink,
  },

  data() {
    const DURATION_1_MONTH = {timeUnit: TIME_UNIT_MONTH, amount: 1};

    return {
      actor: auth.getViewer(),

      loadingStatus: LOADING_STATUS__INIT,

      derbyContainers: null,
      //rewards: [],

      windowDurationOptions: getWindowDurationOptions(),
      windowPastDuration:    DURATION_1_MONTH,
      windowFutureDuration:  DURATION_1_MONTH,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadingStatus === LOADING_STATUS__LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadingStatus === LOADING_STATUS__NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(this.isLoadedNormal && !this.hasDerbies) {
        return 'No derbies.';
      }

      return null;
    },

    hasDerbies() {
      const containers = this.derbyContainers;
      if(containers === null) {
        return false;
      }
      return containers.length > 0;
    },

    /** @returns {boolean} */
    mayManage() {
      return isRoleAdmin(this.actor.role);
    },

    //-------------------------------------------------------------------------
    // Selection
    //-------------------------------------------------------------------------

    /** @returns {boolean} */
    hasSelected() {
      const containers = this.derbyContainers;
      if(containers === null) {
        return false;
      }

      for(const container of containers) {
        if(container.selected) {
          return true;
        }
      }
      return false;
    },

    allSelected: {
      get() {
        for(const container of this.derbyContainers) {
          if(!container.selected) {
            return false;
          }
        }
        return true;
      },

      set(newValue) {
        if(newValue) {
          this.selectAll();
        } else {
          this.deselectAll();
        }
      },
    },

    /** 
     * Идентификаторы выбранных скачек.
     * @returns {number[] | null} 
     */
    selectedDerbyIds() {
      const containers = this.derbyContainers;
      if(containers === null) {
        return null;
      }

      const ids = [];
      for(const container of containers) {
        if(container.selected) {
          ids.push(container.derby.id);
        }
      }

      return ids;
    },

    //-------------------------------------------------------------------------
    // Deletion
    //-------------------------------------------------------------------------

    /** @returns {boolean} */
    isDeleteDerbiesButtonDisabled() {
      return !this.hasSelected;
    },

    /** @returns {string} */
    deleteDerbiesButtonVariant() {
        return this.hasSelected ? 'danger' : 'light';
    }

    //-------------------------------------------------------------------------
  },

  created() {
    this.loadDerbies();
  },

  watch: {
    windowPastDuration() {
      this.loadDerbies();
    },

    windowFutureDuration() {
      this.loadDerbies();
    },
  },

  methods: {
    findDerbyById(derbyId) {
      const containers = this.derbyContainers;
      if(containers === null) {
        return null;
      }

      for(const container of containers) {
        const derby = container.derby;
        if(derby.id === derbyId) {
          return derby;
        }
      }

      return null;
    },

    loadDerbies() {
      notifier.deleteSenderNotifications(this);

      this.loadingStatus = LOADING_STATUS__LOADING;

      const requestConf = {
        params: {}
      };

      const now = convertMomentToStamp(new Date());

      const windowPastDuration = this.windowPastDuration;
      if(windowPastDuration !== null) {
        requestConf.params.begin = getBegin(now, windowPastDuration);
      }

      const windowFutureDuration = this.windowFutureDuration;
      if(windowFutureDuration !== null) {
        requestConf.params.end = getEnd(now, windowFutureDuration);
      }

      HTTP.get('/derbies', requestConf)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        const derbies = data.derbies || [];
        this.derbyContainers = convertDerbiesToDerbyContainers(derbies);

        this.loadingStatus = LOADING_STATUS__NORMAL;
      })
      .catch(err => {
        this.loadingStatus = LOADING_STATUS__ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    getDerbyClass(status) {
      return {
        finished: Derby.isFinished(status)
      };
    },

    statusClass(status) {
      return {
        'status-active': Derby.isActive(status),
      };
    },

    /*
    showRewards(derbyId) {
      HTTP.get(`/derbies/${derbyId}/rewards`)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        const rewards = data.rewards || [];
        rewards.sort((a, b) => a.place - b.place);

        this.rewards = rewards;
      })
      .catch(e => {
        notifier.showError(e, {sender: this});
      })
      .finally(() => {
      });
    },
    */

    //-------------------------------------------------------------------------
    // Selection
    //-------------------------------------------------------------------------

    selectAll() {
      for(const container of this.derbyContainers) {
        if(!container.selected) {
          container.selected = true;
        }
      }
    },

    deselectAll() {
      for(const container of this.derbyContainers) {
        if(container.selected) {
          container.selected = false;
        }
      }
    },

    //-------------------------------------------------------------------------
    // Finish
    //-------------------------------------------------------------------------

    mayFinish(derby) {
      return isRoleAdmin(this.actor.role) && Derby.isActive(derby.status);
    },

    finishDerbyWithConfirmation(derby) {
      notifier.deleteSenderNotifications(this);

      confirmAction(
        this,
        'Are you sure you want to finish this derby {env}?',
        'Finish',
        () => {
          this.finishDerby(derby);
        },
      );
    },

    finishDerby(derby) {
      notifier.deleteSenderNotifications(this);
      
      const url = `/derbies/${derby.id}/finish`;
      HTTP.post(url)
      .then(response => {
        const result = response.data.result;
        if(!result) {
          notifier.showError('Finish the derby failed!', {sender: this});
          return;
        }

        derby.status = result.status;
        notifier.showSuccess('Derby finished.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------
    // Deletion
    //-------------------------------------------------------------------------

    mayDelete(derby) {
      return isRoleAdmin(this.actor.role) && isDerbyDeletable(derby.status);
    },

    findNotDeletableDerbyIdsFromList(derbyIds) {
      const notDeletableDerbyIds = [];

      for(const derbyId of derbyIds) {
        const derby = this.findDerbyById(derbyId);
        if(!isDerbyDeletable(derby.status)) {
          notDeletableDerbyIds.push(derby.id);
        }
      }

      return notDeletableDerbyIds;
    },

    deleteDerbyWithConfirmation(derby) {
      notifier.deleteSenderNotifications(this);

      confirmAction(
        this,
        getDerbyDeletionConfirmationMessage(derby.id),
        'Delete',
        () => {
          this.deleteDerby(derby);
        },
      );
    },

    deleteDerby(derby) {
      const url = `/derby/${derby.id}/delete`;
      HTTP.post(url)
      .then(response => {
        const result = response.data.result;
        if (result.code) {
          notifier.showError(result.message, {sender: this});
          return;
        }

        deleteDerbyContainerFromArray(this.derbyContainers, derby.id);
        notifier.showSuccess('Derby deleted.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------

    deleteDerbiesWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const derbyIds = this.selectedDerbyIds;

      const notDeletableDerbyIds = this.findNotDeletableDerbyIdsFromList(derbyIds);
      if(notDeletableDerbyIds.length > 0) {
        notifier.showError('Можно удалить только запланированные скачики. Если скачки активны или завершены, то их удалить нельзя.', {sender: this});
        return;
      }

      this.confirmDerbiesDeletion(
        derbyIds,
        () => {
          this.deleteDerbies(derbyIds);
        }
      );
    },

    /**
     * @param {number[]} derbyIds
     * @param {ConfirmHandle} onConfirmed
     */
    confirmDerbiesDeletion(derbyIds, onConfirmed) {
      const params = {};
      if(derbyIds.length > 1) {
        params.description = getDerbyIdsInfo(derbyIds);
      }

      confirmAction(
        this,
        getDerbiesDeletionConfirmationMessage(derbyIds),
        'Delete',
        onConfirmed,
        params,
      );
    },

    /** @param {number[]} derbyIds */
    deleteDerbies(derbyIds) {
      const idsStr = derbyIds.join(',');

      const data = new FormData();
      data.set('ids', idsStr);

      HTTP.post('/derbies/delete', data)
      .then(() => {
        deleteDerbyContainersFromArray(this.derbyContainers, derbyIds);

        const message = getDeletionCompleteMessage(derbyIds.length);
        notifier.showSuccess(message);
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------
  },

  filters: {
    statusStr: status => Derby.getStatusName(status),

    audienceName(forQA) {
      return forQA ? 'QA' : 'all';
    },

    getDerbyAudienceNameVerbose,
  },
}

function convertDerbiesToDerbyContainers(derbies) {
  const containers = [];

  for(const derby of derbies) {
    const container = {
      derby: derby,
      selected: false,
    };
    containers.push(container);
  }

  return containers;
}

function getDerbyDeletionConfirmationMessage(id) {
  return `Are you sure you want to delete derby ${id} {env}?`
}

function getDerbiesDeletionConfirmationMessage(ids) {
  if(ids.length === 1) {
    const id = ids[0];
    return getDerbyDeletionConfirmationMessage(id);
  }

  const amount = ids.length;
  return `Are you sure you want to delete ${amount} derbies {env}?`;
}

function getDerbyIdsInfo(ids) {
  return 'Derby ids: ' + ids.join(', ') + '.';
}

/**
 * @param {Object[]} containers 
 * @param {number[]} ids
 */
function deleteDerbyContainersFromArray(containers, ids) {
  for(const id of ids) {
    deleteDerbyContainerFromArray(containers, id);
  }
}

/**
 * @param {Object[]} derbyContainers
 * @param {number} derbyId
 */
 function deleteDerbyContainerFromArray(derbyContainers, derbyId) {
  const index = getDerbyContainerIndex(derbyContainers, derbyId);
  if(index !== null) {
    derbyContainers.splice(index, 1);
  }
}

/**
 * @param {Object[]} derbyContainers
 * @param {number} derbyId
 * @returns {number | null}
 */
 function getDerbyContainerIndex(derbyContainers, derbyId) {
  for(let i = 0; i < derbyContainers.length; i++) {
    const container = derbyContainers[i];
    if(container.derby.id == derbyId) {
      return i;
    }
  }

  return null;
}

function getDeletionCompleteMessage(amount) {
  if(amount === 1) {
    return 'Derby deleted.'
  } else {
    return 'Derbies deleted.'
  }
}
</script>

<style scoped>
.finished {
  color: #dcdcdc;
}

.status-active {
  color: var(--green);
}

.action-box {
  padding: 7px;
}

.action-box > .btn:last-child {
  margin-right: 0 !important;
}
</style>
